/**
 * Find and Replace — the rules behind the dialog, kept pure so each is tested
 * without rendering.
 *
 * Port of legacy ManagePortfolio/ActionButtons.vue (:105-215, :250-407). One
 * From -> To pair per field — Instance Type, Region, Pricing Model, in that
 * order — and no "add another pair", as legacy. The SERVER does the replacing
 * (POST /input/correct); these only build the lists and the request, and fold
 * the answer back into the table.
 */
import { mergeRevalidated, newRowId } from './instanceRows';
import { FIELD, PRICING_MODEL_OPTIONS } from './portfolioFormRules';

/** The three fields, legacy's order and labels; `key` is the request's key. */
export const FIND_REPLACE_FIELDS = Object.freeze([
  Object.freeze({ key: 'instanceType', field: FIELD.INSTANCE_TYPE, label: 'Instance Type' }),
  Object.freeze({ key: 'region', field: FIELD.REGION, label: 'Region' }),
  Object.freeze({ key: 'pricingModel', field: FIELD.PRICING_MODEL, label: 'Pricing Model' }),
]);

/**
 * "Empty" in a From list: the cells that are blank. A sentinel, not '' — ''
 * already means "nothing picked" in a dropdown. Sent to the server as ''.
 */
export const EMPTY_VALUE = '__empty__';
const EMPTY_OPTION = Object.freeze({ value: EMPTY_VALUE, label: 'Empty' });

const PRICING_LABELS = Object.fromEntries(PRICING_MODEL_OPTIONS.map((o) => [o.value, o.label]));

/** Every pair unset — what the dialog opens with (legacy resets all six). */
export function emptyPairs() {
  return Object.fromEntries(FIND_REPLACE_FIELDS.map(({ key }) => [key, { from: '', to: '' }]));
}

/**
 * The From list for one field: the values IN THE TABLE, each once, in table
 * order (legacy :373-396). Blank cells are "Empty". Pricing Model shows its
 * labels ("reserved (1 year)") and leaves out 'spot', as legacy does.
 */
export function fromOptions(rows, field) {
  const seen = new Set();
  const options = [];
  for (const row of rows) {
    const raw = row?.[field];
    const blank = raw === null || raw === undefined || String(raw).trim() === '';
    const value = blank ? EMPTY_VALUE : String(raw);
    if (field === FIELD.PRICING_MODEL && value === 'spot') continue;
    if (seen.has(value)) continue;
    seen.add(value);
    if (blank) options.push(EMPTY_OPTION);
    else
      options.push({
        value,
        label: field === FIELD.PRICING_MODEL ? (PRICING_LABELS[value] ?? value) : value,
      });
  }
  return options;
}

/** The Pricing Model To list — the three models, labelled; values are the API's. */
export const PRICING_TO_OPTIONS = PRICING_MODEL_OPTIONS;

/**
 * Replace all is allowed when every pair is either complete or untouched, and
 * at least one is complete (legacy isReplaceAllDisabled :397-407).
 */
export function isReplaceReady(pairs) {
  let complete = 0;
  for (const { key } of FIND_REPLACE_FIELDS) {
    const { from = '', to = '' } = pairs?.[key] ?? {};
    if (Boolean(from) !== Boolean(to)) return false;
    if (from && to) complete += 1;
  }
  return complete > 0;
}

/** The pairs as the server takes them: all three keys, '' for Empty or unused. */
export function toCorrectPairs(pairs) {
  const plain = (v) => (!v || v === EMPTY_VALUE ? '' : v);
  return Object.fromEntries(
    FIND_REPLACE_FIELDS.map(({ key }) => [
      key,
      { from: plain(pairs?.[key]?.from), to: plain(pairs?.[key]?.to) },
    ]),
  );
}

/**
 * The table after the server's answer. Row for row when it sent back as many
 * rows as it got — each keeps its table id, and fields the server leaves out
 * (OCI OCPU / Memory, which legacy's mapping dropped) survive. Otherwise the
 * answer simply becomes the list, as legacy does. Every row is unsaved.
 */
export function mergeCorrectedRows(sentRows, correctedRows) {
  if (correctedRows.length === sentRows.length) {
    return correctedRows.map((row, i) => mergeRevalidated(sentRows[i], row));
  }
  return correctedRows.map((row) => mergeRevalidated({ _rowId: newRowId() }, row));
}
